import { getChartLayout } from "./layout";
import { plotWidth } from "./state";
import { priceToY, timeToX, xToTime, yToPrice } from "./transformation";
import type { DrawingObject, DrawingPoint, DrawingToolType, State } from "./types";
import { getDrawingDefaultColor, getTheme } from "./theme";
import { scheduleChartPersistence } from "./persistence";

const HIT_THRESHOLD = 8;
const HANDLE_RADIUS = 5;
const MAX_DRAWINGS = 50;
const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1] as const;
const FIB_EXT_LEVELS = [0, 0.618, 1, 1.382, 1.618, 2, 2.618] as const;
const FIB_COLORS: Record<number, string> = {
  0: "#94a3b8",
  0.236: "#ef4444",
  0.382: "#f97316",
  0.5: "#eab308",
  0.618: "#22c55e",
  0.786: "#3b82f6",
  1: "#a855f7",
};
const FIB_EXT_COLORS: Record<number, string> = {
  0: "#94a3b8",
  0.618: "#22c55e",
  1: "#3b82f6",
  1.382: "#f97316",
  1.618: "#ef4444",
  2: "#a855f7",
  2.618: "#ec4899",
};

export const DRAWING_TOOL_CHANGE_EVENT = "chart-learning:drawing-tool-change";

function newId(): string {
  return `drawing-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function notifyDrawingToolChange(): void {
  window.dispatchEvent(new CustomEvent(DRAWING_TOOL_CHANGE_EVENT));
}

function returnToCursor(state: State): void {
  state.activeDrawingTool = null;
  notifyDrawingToolChange();
  scheduleChartPersistence(state);
}

function requiredPoints(type: DrawingToolType): number {
  if (type === "horizontal" || type === "vertical" || type === "text" || type === "priceLabel") return 1;
  if (type === "triangle" || type === "fibExtension") return 3;
  return 2;
}

function pointerToPoint(state: State, x: number, y: number): DrawingPoint {
  const { mainState } = getChartLayout(state);
  return { time: xToTime(mainState, x), price: yToPrice(mainState, y) };
}

function createDrawing(state: State, type: DrawingToolType, point: DrawingPoint): DrawingObject {
  const base = {
    id: newId(),
    type,
    color: getDrawingDefaultColor(state, type),
    lineWidth: 2,
    visible: true,
  };

  if (type === "text") return { ...base, points: [point], state: "complete", text: "Label" };
  if (requiredPoints(type) > 1) return { ...base, points: [point, point], state: "drawing" };
  return { ...base, points: [point], state: "complete" };
}

function distance(px: number, py: number, x1: number, y1: number): number {
  return Math.hypot(px - x1, py - y1);
}

function distanceToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return distance(px, py, x1, y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  return distance(px, py, x1 + t * dx, y1 + t * dy);
}

function distanceToRay(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return distance(px, py, x1, y1);
  const t = Math.max(0, ((px - x1) * dx + (py - y1) * dy) / lenSq);
  return distance(px, py, x1 + t * dx, y1 + t * dy);
}

function getRayEnd(x1: number, y1: number, x2: number, y2: number, minX: number, maxX: number): { x: number; y: number } {
  const dx = x2 - x1;
  if (Math.abs(dx) < 1e-6) return { x: x1, y: y2 > y1 ? 1e6 : -1e6 };
  const targetX = dx > 0 ? maxX : minX;
  const t = (targetX - x1) / dx;
  return { x: targetX, y: y1 + (y2 - y1) * t };
}

function extendToBounds(
  ox: number,
  oy: number,
  tx: number,
  ty: number,
  x0: number,
  x1: number,
  y0: number,
  y1: number,
): { x: number; y: number } {
  const dx = tx - ox;
  const dy = ty - oy;
  if (dx === 0 && dy === 0) return { x: tx, y: ty };

  let t = Infinity;
  if (dx > 0) t = Math.min(t, (x1 - ox) / dx);
  if (dx < 0) t = Math.min(t, (x0 - ox) / dx);
  if (dy > 0) t = Math.min(t, (y1 - oy) / dy);
  if (dy < 0) t = Math.min(t, (y0 - oy) / dy);

  if (!isFinite(t) || t <= 0) return { x: tx, y: ty };
  return { x: ox + dx * t, y: oy + dy * t };
}

function drawHandle(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(x, y, HANDLE_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.max(0, Math.min(r, Math.min(w, h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.arcTo(x + w, y, x + w, y + radius, radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
  ctx.lineTo(x + radius, y + h);
  ctx.arcTo(x, y + h, x, y + h - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
}

function getTextBoxDimensions(text: string): { width: number; height: number; paddingX: number; paddingY: number } {
  const paddingX = 8;
  const paddingY = 5;
  const approxTextWidth = Math.max(30, text.length * 7.5);
  return { width: approxTextWidth + paddingX * 2, height: 13 + paddingY * 2, paddingX, paddingY };
}

function getHandleAtPoint(state: State, drawing: DrawingObject, x: number, y: number): number | null {
  const { mainState } = getChartLayout(state);

  if (drawing.type === "horizontal") {
    const lineY = priceToY(mainState, drawing.points[0].price);
    const handleX = mainState.left + plotWidth(mainState) / 2;
    return distance(x, y, handleX, lineY) <= HIT_THRESHOLD ? 0 : null;
  }

  if (drawing.type === "vertical") {
    const lineX = timeToX(mainState, drawing.points[0].time);
    const handleY = mainState.top + (mainState.height - mainState.top - mainState.bottom) / 2;
    return distance(x, y, lineX, handleY) <= HIT_THRESHOLD ? 0 : null;
  }

  if (drawing.type === "priceLabel") {
    const yLine = priceToY(mainState, drawing.points[0].price);
    const xMid = mainState.left + plotWidth(mainState) / 2;
    return distance(x, y, xMid, yLine) <= HIT_THRESHOLD ? 0 : null;
  }

  for (let i = 0; i < drawing.points.length; i++) {
    const point = drawing.points[i];
    const px = timeToX(mainState, point.time);
    const py = priceToY(mainState, point.price);
    if (distance(x, y, px, py) <= HIT_THRESHOLD) return i;
  }
  return null;
}

function isPointInTriangle(px: number, py: number, x1: number, y1: number, x2: number, y2: number, x3: number, y3: number): boolean {
  const area = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number) => {
    const termA = ax * (by - cy);
    const termB = bx * (cy - ay);
    const termC = cx * (ay - by);
    const signedDoubleArea = termA + termB + termC;
    return signedDoubleArea / 2;
  };

  const a = Math.abs(area(x1, y1, x2, y2, x3, y3));
  const a1 = Math.abs(area(px, py, x2, y2, x3, y3));
  const a2 = Math.abs(area(x1, y1, px, py, x3, y3));
  const a3 = Math.abs(area(x1, y1, x2, y2, px, py));
  return Math.abs(a - (a1 + a2 + a3)) < 1;
}

function isDrawingNearPoint(state: State, drawing: DrawingObject, x: number, y: number): boolean {
  const { mainState } = getChartLayout(state);
  if (!drawing.visible || drawing.points.length === 0) return false;

  const x0 = mainState.left;
  const x1Plot = mainState.left + plotWidth(mainState);

  if (drawing.type === "horizontal") {
    const lineY = priceToY(mainState, drawing.points[0].price);
    return x >= x0 && x <= x1Plot && Math.abs(y - lineY) <= HIT_THRESHOLD;
  }

  if (drawing.type === "vertical") {
    const lineX = timeToX(mainState, drawing.points[0].time);
    const y0 = mainState.top;
    const y1 = mainState.height - mainState.bottom;
    return y >= y0 && y <= y1 && Math.abs(x - lineX) <= HIT_THRESHOLD;
  }

  if (drawing.type === "text") {
    const p = drawing.points[0];
    const tx = timeToX(mainState, p.time);
    const ty = priceToY(mainState, p.price);
    const dims = getTextBoxDimensions(drawing.text ?? "Label");
    return x >= tx - HIT_THRESHOLD && x <= tx + dims.width + HIT_THRESHOLD && y >= ty - HIT_THRESHOLD && y <= ty + dims.height + HIT_THRESHOLD;
  }

  if (drawing.type === "priceLabel") {
    const yLine = priceToY(mainState, drawing.points[0].price);
    const nearLine = x >= x0 && x <= x1Plot && Math.abs(y - yLine) <= HIT_THRESHOLD;
    const badgeX = x1Plot + 4;
    const badgeW = 70;
    const badgeH = 18;
    const nearBadge = x >= badgeX - HIT_THRESHOLD && x <= badgeX + badgeW + HIT_THRESHOLD && Math.abs(y - yLine) <= badgeH / 2 + HIT_THRESHOLD;
    return nearLine || nearBadge;
  }

  if (drawing.points.length < 2) return false;

  const p1 = drawing.points[0];
  const p2 = drawing.points[1];
  const sx1 = timeToX(mainState, p1.time);
  const sy1 = priceToY(mainState, p1.price);
  const sx2 = timeToX(mainState, p2.time);
  const sy2 = priceToY(mainState, p2.price);

  if (drawing.type === "trendline") return distanceToSegment(x, y, sx1, sy1, sx2, sy2) <= HIT_THRESHOLD;
  if (drawing.type === "ray") return distanceToRay(x, y, sx1, sy1, sx2, sy2) <= HIT_THRESHOLD;

  if (drawing.type === "channel") {
    const left = Math.min(sx1, sx2);
    const right = Math.max(sx1, sx2);
    const top = Math.min(sy1, sy2);
    const bottom = Math.max(sy1, sy2);
    const onTop = Math.abs(y - top) <= HIT_THRESHOLD && x >= left - HIT_THRESHOLD && x <= right + HIT_THRESHOLD;
    const onBottom = Math.abs(y - bottom) <= HIT_THRESHOLD && x >= left - HIT_THRESHOLD && x <= right + HIT_THRESHOLD;
    const inside = x >= left && x <= right && y >= top && y <= bottom;
    return onTop || onBottom || inside;
  }

  if (drawing.type === "extendedRectangle") {
    const left = Math.min(sx1, sx2);
    const right = x1Plot;
    const top = Math.min(sy1, sy2);
    const bottom = Math.max(sy1, sy2);
    const onLeft = Math.abs(x - left) <= HIT_THRESHOLD && y >= top - HIT_THRESHOLD && y <= bottom + HIT_THRESHOLD;
    const onTop = Math.abs(y - top) <= HIT_THRESHOLD && x >= left - HIT_THRESHOLD && x <= right + HIT_THRESHOLD;
    const onBottom = Math.abs(y - bottom) <= HIT_THRESHOLD && x >= left - HIT_THRESHOLD && x <= right + HIT_THRESHOLD;
    const inside = x >= left && x <= right && y >= top && y <= bottom;
    return onLeft || onTop || onBottom || inside;
  }

  if (drawing.type === "ellipse") {
    const cx = (sx1 + sx2) / 2;
    const cy = (sy1 + sy2) / 2;
    const rx = Math.max(1, Math.abs(sx2 - sx1) / 2);
    const ry = Math.max(1, Math.abs(sy2 - sy1) / 2);
    const norm = ((x - cx) * (x - cx)) / (rx * rx) + ((y - cy) * (y - cy)) / (ry * ry);
    return Math.abs(norm - 1) <= 0.2 || norm < 1.05;
  }

  if (drawing.type === "fibRetrace") {
    const swing = p2.price - p1.price;
    for (const level of FIB_LEVELS) {
      const py = priceToY(mainState, p1.price + swing * level);
      if (x >= x0 && x <= x1Plot && Math.abs(y - py) <= HIT_THRESHOLD) return true;
    }
    return distanceToSegment(x, y, sx1, sy1, sx2, sy2) <= HIT_THRESHOLD;
  }

  if (drawing.type === "fibFan") {
    const swing = p2.price - p1.price;
    for (const level of FIB_LEVELS) {
      const ty = priceToY(mainState, p1.price + swing * level);
      const far = extendToBounds(sx1, sy1, sx2, ty, x0, x1Plot, mainState.top, mainState.height - mainState.bottom);
      if (distanceToSegment(x, y, sx1, sy1, far.x, far.y) <= HIT_THRESHOLD) return true;
    }
    return false;
  }

  if (drawing.type === "fibExtension") {
    const base = drawing.points.length >= 3 ? drawing.points[2] : p2;
    const swing = p2.price - p1.price;
    for (const level of FIB_EXT_LEVELS) {
      const py = priceToY(mainState, base.price + swing * level);
      if (x >= x0 && x <= x1Plot && Math.abs(y - py) <= HIT_THRESHOLD) return true;
    }
    const bX = timeToX(mainState, base.time);
    const bY = priceToY(mainState, base.price);
    return distanceToSegment(x, y, sx1, sy1, sx2, sy2) <= HIT_THRESHOLD || distanceToSegment(x, y, sx2, sy2, bX, bY) <= HIT_THRESHOLD;
  }

  if (drawing.type === "polyline") {
    for (let i = 0; i < drawing.points.length - 1; i++) {
      const a = drawing.points[i];
      const b = drawing.points[i + 1];
      const ax = timeToX(mainState, a.time);
      const ay = priceToY(mainState, a.price);
      const bx = timeToX(mainState, b.time);
      const by = priceToY(mainState, b.price);
      if (distanceToSegment(x, y, ax, ay, bx, by) <= HIT_THRESHOLD) return true;
    }
    return false;
  }

  if (drawing.type === "triangle" && drawing.points.length >= 3) {
    const p3 = drawing.points[2];
    const sx3 = timeToX(mainState, p3.time);
    const sy3 = priceToY(mainState, p3.price);
    const nearEdge =
      distanceToSegment(x, y, sx1, sy1, sx2, sy2) <= HIT_THRESHOLD ||
      distanceToSegment(x, y, sx2, sy2, sx3, sy3) <= HIT_THRESHOLD ||
      distanceToSegment(x, y, sx3, sy3, sx1, sy1) <= HIT_THRESHOLD;
    return nearEdge || isPointInTriangle(x, y, sx1, sy1, sx2, sy2, sx3, sy3);
  }

  return false;
}

function findDrawingAtPoint(state: State, x: number, y: number): DrawingObject | null {
  for (let i = state.drawings.length - 1; i >= 0; i--) {
    const drawing = state.drawings[i];
    if (isDrawingNearPoint(state, drawing, x, y)) return drawing;
  }
  return null;
}

export function handleDrawingMouseDown(state: State, x: number, y: number, clickCount = 1): boolean {
  const point = pointerToPoint(state, x, y);

  if (state.activeDrawingTool) {
    if (state.drawings.length >= MAX_DRAWINGS) return true;

    if (!state.currentDrawing) {
      const drawing = createDrawing(state, state.activeDrawingTool, point);
      state.selectedDrawingId = drawing.id;
      if (drawing.state === "complete") {
        state.drawings = [...state.drawings, drawing];
        scheduleChartPersistence(state);
        returnToCursor(state);
      } else {
        state.currentDrawing = drawing;
      }
      return true;
    }

    if (state.currentDrawing.type === "polyline") {
      const points = state.currentDrawing.points;
      const lastIndex = points.length - 1;
      points[lastIndex] = point;
      if (clickCount >= 2) {
        if (points.length > 1) points.pop();
        state.currentDrawing.state = "complete";
        state.drawings = [...state.drawings, state.currentDrawing];
        state.selectedDrawingId = state.currentDrawing.id;
        state.currentDrawing = null;
        scheduleChartPersistence(state);
        returnToCursor(state);
      } else {
        points.push(point);
      }
      return true;
    }

    const needed = requiredPoints(state.currentDrawing.type);
    const points = state.currentDrawing.points;
    const lastIndex = points.length - 1;
    points[lastIndex] = point;

    if (points.length < needed) {
      points.push(point);
    } else {
      state.currentDrawing.state = "complete";
      state.drawings = [...state.drawings, state.currentDrawing];
      state.selectedDrawingId = state.currentDrawing.id;
      state.currentDrawing = null;
      scheduleChartPersistence(state);
      returnToCursor(state);
    }

    return true;
  }

  const drawing = findDrawingAtPoint(state, x, y);
  if (!drawing) {
    const hadSelection = state.selectedDrawingId !== null;
    state.selectedDrawingId = null;
    return hadSelection;
  }

  const handleIndex = getHandleAtPoint(state, drawing, x, y) ?? -1;
  state.selectedDrawingId = drawing.id;
  state.draggingDrawing = {
    id: drawing.id,
    handleIndex,
    startPoint: point,
    startPoints: drawing.points.map((p) => ({ ...p })),
  };
  return true;
}

export function handleDrawingMouseMove(state: State, x: number, y: number): boolean {
  const point = pointerToPoint(state, x, y);

  if (state.draggingDrawing) {
    const drawing = state.drawings.find((item) => item.id === state.draggingDrawing?.id);
    if (!drawing) return false;

    if (state.draggingDrawing.handleIndex === -1) {
      const dt = point.time - state.draggingDrawing.startPoint.time;
      const dp = point.price - state.draggingDrawing.startPoint.price;
      if (drawing.type === "horizontal" || drawing.type === "priceLabel") {
        drawing.points = state.draggingDrawing.startPoints.map((p) => ({ ...p, price: p.price + dp }));
      } else if (drawing.type === "vertical") {
        drawing.points = state.draggingDrawing.startPoints.map((p) => ({ ...p, time: p.time + dt }));
      } else {
        drawing.points = state.draggingDrawing.startPoints.map((p) => ({ time: p.time + dt, price: p.price + dp }));
      }
    } else if (drawing.type === "horizontal" || drawing.type === "priceLabel") {
      drawing.points[0].price = point.price;
    } else if (drawing.type === "vertical") {
      drawing.points[0].time = point.time;
    } else if (drawing.type === "channel" && drawing.points.length >= 2 && state.draggingDrawing.handleIndex <= 1) {
      drawing.points[state.draggingDrawing.handleIndex] = point;
    } else {
      drawing.points[state.draggingDrawing.handleIndex] = point;
    }
    scheduleChartPersistence(state);
    return true;
  }

  if (state.currentDrawing) {
    const lastIndex = state.currentDrawing.points.length - 1;
    state.currentDrawing.points[lastIndex] = point;
    return true;
  }

  const hovered = findDrawingAtPoint(state, x, y);
  const nextId = hovered?.id ?? null;
  if (nextId !== state.hoveredDrawingId) {
    state.hoveredDrawingId = nextId;
    return true;
  }

  return Boolean(state.activeDrawingTool);
}

export function handleDrawingMouseUp(state: State): boolean {
  if (!state.draggingDrawing) return false;
  state.draggingDrawing = null;
  scheduleChartPersistence(state);
  return true;
}

export function getDrawingCursor(state: State): string | null {
  if (state.draggingDrawing) return "grabbing";
  if (state.activeDrawingTool || state.currentDrawing) return "crosshair";
  if (state.hoveredDrawingId) return "grab";
  return null;
}

export function deleteSelectedDrawing(state: State): boolean {
  if (!state.selectedDrawingId) return false;
  const next = state.drawings.filter((drawing) => drawing.id !== state.selectedDrawingId);
  if (next.length === state.drawings.length) return false;

  state.drawings = next;
  state.selectedDrawingId = null;
  state.hoveredDrawingId = null;
  state.draggingDrawing = null;
  scheduleChartPersistence(state);
  return true;
}

export function startEditingSelectedTextDrawing(state: State, container: HTMLElement, redraw: () => void): boolean {
  if (!state.selectedDrawingId) return false;
  const drawing = state.drawings.find((item) => item.id === state.selectedDrawingId);
  if (!drawing || drawing.type !== "text" || drawing.points.length === 0) return false;

  const existingEditor = container.querySelector<HTMLInputElement>('[data-drawing-text-editor="true"]');
  if (existingEditor) existingEditor.remove();

  const { mainState } = getChartLayout(state);
  const anchor = drawing.points[0];
  const x = timeToX(mainState, anchor.time);
  const y = priceToY(mainState, anchor.price);
  const dims = getTextBoxDimensions(drawing.text ?? "Label");

  const input = document.createElement("input");
  input.type = "text";
  input.value = drawing.text ?? "Label";
  input.setAttribute("data-drawing-text-editor", "true");
  input.style.position = "absolute";
  input.style.left = `${Math.round(x)}px`;
  input.style.top = `${Math.round(y)}px`;
  input.style.width = `${Math.max(90, Math.round(dims.width + 20))}px`;
  input.style.height = `${Math.round(dims.height + 4)}px`;
  input.style.padding = "4px 8px";
  const tt = getTheme(state);
  input.style.border = `1px solid ${tt.crosshair}`;
  input.style.borderRadius = "6px";
  input.style.font = "13px monospace";
  input.style.background = tt.axisBand;
  input.style.color = tt.text;
  input.style.zIndex = "20";
  input.style.outline = "none";

  let committed = false;
  const commit = () => {
    if (committed) return;
    committed = true;
    const next = input.value.trim();
    drawing.text = next.length > 0 ? next : "Label";
    input.remove();
    scheduleChartPersistence(state);
    redraw();
  };
  const cancel = () => {
    if (committed) return;
    committed = true;
    input.remove();
    redraw();
  };

  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  });
  input.addEventListener("blur", commit);

  container.appendChild(input);
  input.focus();
  input.select();
  return true;
}

export function renderDrawings(ctx: CanvasRenderingContext2D, state: State): void {
  const t = getTheme(state);
  const { mainState } = getChartLayout(state);
  const y0 = mainState.top;
  const y1 = mainState.height - mainState.bottom;
  const x0 = mainState.left;
  const x1Plot = mainState.left + plotWidth(mainState);
  const items = state.currentDrawing ? [...state.drawings, state.currentDrawing] : state.drawings;

  ctx.save();
  ctx.beginPath();
  ctx.rect(x0, y0, x1Plot - x0, y1 - y0);
  ctx.clip();

  for (const drawing of items) {
    if (!drawing.visible || drawing.points.length === 0) continue;

    const selected = state.selectedDrawingId === drawing.id;
    const hovered = state.hoveredDrawingId === drawing.id;
    const lineWidth = hovered || selected ? drawing.lineWidth + 1 : drawing.lineWidth;

    ctx.save();
    ctx.strokeStyle = drawing.color;
    ctx.fillStyle = `${drawing.color}22`;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    if (drawing.type === "horizontal") {
      const y = priceToY(mainState, drawing.points[0].price);
      ctx.beginPath();
      ctx.moveTo(x0, y);
      ctx.lineTo(x1Plot, y);
      ctx.stroke();
      if (hovered || selected) drawHandle(ctx, x0 + (x1Plot - x0) / 2, y, drawing.color);
    } else if (drawing.type === "vertical") {
      const x = timeToX(mainState, drawing.points[0].time);
      ctx.beginPath();
      ctx.moveTo(x, y0);
      ctx.lineTo(x, y1);
      ctx.stroke();
      if (hovered || selected) drawHandle(ctx, x, y0 + (y1 - y0) / 2, drawing.color);
    } else if (drawing.type === "text") {
      const p = drawing.points[0];
      const tx = timeToX(mainState, p.time);
      const ty = priceToY(mainState, p.price);
      const text = drawing.text ?? "Label";
      const dims = getTextBoxDimensions(text);

      ctx.fillStyle = t.axisBand;
      ctx.strokeStyle = hovered || selected ? t.crosshair : t.grid;
      ctx.lineWidth = hovered || selected ? 2 : 1.5;
      roundRect(ctx, tx, ty, dims.width, dims.height, 4);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = drawing.color;
      ctx.font = "13px monospace";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(text, tx + dims.paddingX, ty + dims.paddingY);
      if (hovered || selected) drawHandle(ctx, tx, ty, t.crosshair);
    } else if (drawing.type === "priceLabel") {
      const price = drawing.points[0].price;
      const y = priceToY(mainState, price);
      const label = price.toFixed(2);

      ctx.strokeStyle = hovered || selected ? drawing.color : `${drawing.color}80`;
      ctx.lineWidth = hovered || selected ? 1.5 : 1;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(x0, y);
      ctx.lineTo(x1Plot, y);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.font = "bold 11px monospace";
      const textW = ctx.measureText(label).width;
      const boxW = textW + 16;
      const boxH = 18;
      const boxX = x1Plot + 4;
      const boxY = y - boxH / 2;
      ctx.fillStyle = drawing.color;
      roundRect(ctx, boxX, boxY, boxW, boxH, boxH / 2);
      ctx.fill();
      if (hovered || selected) {
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 1.2;
        ctx.stroke();
      }
      ctx.fillStyle = "#fff";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(label, boxX + 8, y);
      ctx.beginPath();
      ctx.moveTo(x1Plot + 2, y);
      ctx.lineTo(boxX + 2, y - 4);
      ctx.lineTo(boxX + 2, y + 4);
      ctx.closePath();
      ctx.fillStyle = drawing.color;
      ctx.fill();
      if (hovered || selected) drawHandle(ctx, x0 + (x1Plot - x0) / 2, y, drawing.color);
    } else if (drawing.points.length >= 2) {
      const p1 = drawing.points[0];
      const p2 = drawing.points[1];
      const sx1 = timeToX(mainState, p1.time);
      const sy1 = priceToY(mainState, p1.price);
      const sx2 = timeToX(mainState, p2.time);
      const sy2 = priceToY(mainState, p2.price);

      if (drawing.type === "trendline") {
        ctx.beginPath();
        ctx.moveTo(sx1, sy1);
        ctx.lineTo(sx2, sy2);
        ctx.stroke();
      } else if (drawing.type === "ray") {
        const end = getRayEnd(sx1, sy1, sx2, sy2, x0, x1Plot);
        ctx.beginPath();
        ctx.moveTo(sx1, sy1);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      } else if (drawing.type === "extendedRectangle") {
        const left = Math.min(sx1, sx2);
        const top = Math.min(sy1, sy2);
        const height = Math.abs(sy2 - sy1);
        ctx.beginPath();
        ctx.rect(left, top, x1Plot - left, height);
        ctx.fill();
        ctx.stroke();
      } else if (drawing.type === "ellipse") {
        const cx = (sx1 + sx2) / 2;
        const cy = (sy1 + sy2) / 2;
        const rx = Math.abs(sx2 - sx1) / 2;
        const ry = Math.abs(sy2 - sy1) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      } else if (drawing.type === "triangle") {
        const p3 = drawing.points[2];
        if (p3) {
          const sx3 = timeToX(mainState, p3.time);
          const sy3 = priceToY(mainState, p3.price);
          ctx.beginPath();
          ctx.moveTo(sx1, sy1);
          ctx.lineTo(sx2, sy2);
          ctx.lineTo(sx3, sy3);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
        } else {
          ctx.beginPath();
          ctx.moveTo(sx1, sy1);
          ctx.lineTo(sx2, sy2);
          ctx.stroke();
        }
      } else if (drawing.type === "channel") {
        const left = Math.min(sx1, sx2);
        const right = Math.max(sx1, sx2);
        const top = Math.min(sy1, sy2);
        const bottom = Math.max(sy1, sy2);
        ctx.fillRect(left, top, right - left, bottom - top);
        ctx.beginPath();
        ctx.moveTo(left, top);
        ctx.lineTo(right, top);
        ctx.moveTo(left, bottom);
        ctx.lineTo(right, bottom);
        ctx.stroke();
      } else if (drawing.type === "fibRetrace") {
        const swing = p2.price - p1.price;
        const levelYs: Array<{ level: (typeof FIB_LEVELS)[number]; y: number }> = [];
        for (const level of FIB_LEVELS) {
          levelYs.push({ level, y: priceToY(mainState, p1.price + swing * level) });
        }
        for (let i = 0; i < levelYs.length - 1; i++) {
          const yA = levelYs[i].y;
          const yB = levelYs[i + 1].y;
          const fillColor = `${FIB_COLORS[levelYs[i].level]}22`;
          ctx.fillStyle = fillColor;
          ctx.fillRect(x0, Math.min(yA, yB), x1Plot - x0, Math.abs(yB - yA));
        }
        for (const level of FIB_LEVELS) {
          const py = priceToY(mainState, p1.price + swing * level);
          ctx.strokeStyle = FIB_COLORS[level];
          ctx.setLineDash(level === 0 || level === 1 ? [] : [5, 3]);
          ctx.beginPath();
          ctx.moveTo(x0, py);
          ctx.lineTo(x1Plot, py);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = FIB_COLORS[level];
          ctx.font = "10px monospace";
          ctx.textAlign = "right";
          ctx.fillText(`${(level * 100).toFixed(level === 0 || level === 1 ? 0 : 1)}%`, x1Plot - 4, py - 3);
        }
        ctx.strokeStyle = `${drawing.color}66`;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(sx1, sy1);
        ctx.lineTo(sx2, sy2);
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (drawing.type === "fibFan") {
        const swing = p2.price - p1.price;
        const rays: Array<{ level: (typeof FIB_LEVELS)[number]; farX: number; farY: number }> = [];
        for (const level of FIB_LEVELS) {
          const ty = priceToY(mainState, p1.price + swing * level);
          const far = extendToBounds(sx1, sy1, sx2, ty, x0, x1Plot, y0, y1);
          rays.push({ level, farX: far.x, farY: far.y });
        }
        for (let i = 0; i < rays.length - 1; i++) {
          const a = rays[i];
          const b = rays[i + 1];
          ctx.fillStyle = `${FIB_COLORS[a.level]}22`;
          ctx.beginPath();
          ctx.moveTo(sx1, sy1);
          ctx.lineTo(a.farX, a.farY);
          ctx.lineTo(b.farX, b.farY);
          ctx.closePath();
          ctx.fill();
        }
        for (const ray of rays) {
          ctx.strokeStyle = FIB_COLORS[ray.level];
          const level = ray.level;
          ctx.lineWidth = level === 0 || level === 1 ? 1.5 : 1;
          ctx.setLineDash(level === 0 || level === 1 ? [4, 3] : []);
          ctx.beginPath();
          ctx.moveTo(sx1, sy1);
          ctx.lineTo(ray.farX, ray.farY);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      } else if (drawing.type === "fibExtension") {
        const base = drawing.points.length >= 3 ? drawing.points[2] : p2;
        const baseY = priceToY(mainState, base.price);
        const swing = p2.price - p1.price;
        const levelYs: Array<{ level: (typeof FIB_EXT_LEVELS)[number]; y: number }> = [];
        for (const level of FIB_EXT_LEVELS) {
          levelYs.push({ level, y: priceToY(mainState, base.price + swing * level) });
        }
        for (let i = 0; i < levelYs.length - 1; i++) {
          const yA = levelYs[i].y;
          const yB = levelYs[i + 1].y;
          ctx.fillStyle = `${FIB_EXT_COLORS[levelYs[i].level]}22`;
          ctx.fillRect(x0, Math.min(yA, yB), x1Plot - x0, Math.abs(yB - yA));
        }
        for (const level of FIB_EXT_LEVELS) {
          const py = priceToY(mainState, base.price + swing * level);
          ctx.strokeStyle = FIB_EXT_COLORS[level];
          ctx.setLineDash(level === 0 ? [] : [5, 3]);
          ctx.beginPath();
          ctx.moveTo(x0, py);
          ctx.lineTo(x1Plot, py);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = FIB_EXT_COLORS[level];
          ctx.font = "10px monospace";
          ctx.textAlign = "right";
          ctx.fillText(`${(level * 100).toFixed(1)}%`, x1Plot - 4, py - 3);
        }
        ctx.strokeStyle = `${drawing.color}66`;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(sx1, sy1);
        ctx.lineTo(sx2, sy2);
        ctx.lineTo(timeToX(mainState, base.time), baseY);
        ctx.stroke();
        ctx.setLineDash([]);
      } else if (drawing.type === "polyline") {
        ctx.beginPath();
        ctx.moveTo(sx1, sy1);
        for (let i = 1; i < drawing.points.length; i++) {
          const p = drawing.points[i];
          ctx.lineTo(timeToX(mainState, p.time), priceToY(mainState, p.price));
        }
        ctx.stroke();
      }

      if (hovered || selected || drawing.state === "drawing") {
        for (const point of drawing.points) {
          drawHandle(ctx, timeToX(mainState, point.time), priceToY(mainState, point.price), drawing.color);
        }
      }
    }

    ctx.restore();
  }

  ctx.restore();
}
